// Authentication Context and API utilities
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const AuthContext = createContext(null);

function resolveApiBase() {
  try {
    if (typeof window !== "undefined") {
      const h = (window.location.hostname || "").toLowerCase();
      const port = String(window.location.port || "");
      // Production domain / HTTPS: same-origin (nginx /api proxy). Empty = relative.
      if (h === "cgnews24.co.in" || h === "www.cgnews24.co.in") {
        return "";
      }
      if (window.location.protocol === "https:") {
        return "";
      }
      // Direct IP or localhost HTTP -> backend :8001
      if (h === "69.169.97.198" || h === "localhost" || h === "127.0.0.1") {
        return "http://" + h + ":8001";
      }
      if (port === "3000" && h) {
        return "http://" + h + ":8001";
      }
    }
  } catch (e) {}
  const env = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (env && env.indexOf("69.169.97.198") === -1) return env;
  return "";
}
export const API_BASE_URL = resolveApiBase();

const apiPath = (endpoint) => {
  if (endpoint.startsWith("/api/")) return endpoint;
  if (endpoint.startsWith("/")) return `/api${endpoint}`;
  return `/api/${endpoint}`;
};

// Used by pages as axios base: `${API}/billing/status` etc.
export const API = (() => {
  const b = (API_BASE_URL || "").replace(/\/$/, "");
  if (!b) return "/api";
  if (b.endsWith("/api")) return b;
  return `${b}/api`;
})();

export const apiClient = {
  async request(endpoint, options = {}) {
    const token = localStorage.getItem("token");
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${apiPath(endpoint)}`, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      const error = await response.json().catch(() => ({ detail: "Unauthorized" }));
      const detail = error.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
            : "Unauthorized";
      // Login/register: show real message, do not hard-redirect mid-submit
      const isAuthForm = String(endpoint || "").includes("/auth/login") || String(endpoint || "").includes("/auth/register");
      if (token && !isAuthForm) {
        localStorage.removeItem("token");
        window.location.href = "/";
      }
      throw new Error(msg);
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: "Request failed" }));
      const detail = error.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ")
            : "Request failed";
      throw new Error(msg);
    }

    return response.json();
  },

  login: (email, password) =>
    apiClient.request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  register: (data) =>
    apiClient.request("/auth/register", {
      method: "POST",
      body: JSON.stringify(data),
    }),

  getProfile: () => apiClient.request("/auth/me"),

  logout: () => {
    localStorage.removeItem("token");
  },
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(true);

  const applyAuthPayload = useCallback((data) => {
    // /auth/me returns { user, subscribed } or sometimes just user fields
    const u = data?.user || data;
    const sub =
      typeof data?.subscribed === "boolean"
        ? data.subscribed
        : u?.role === "admin" ||
          u?.subscription_active === true ||
          (!!u?.trial_ends_at && new Date(u.trial_ends_at) > new Date());
    setUser(u && u.email ? u : null);
    setSubscribed(!!sub);
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setUser(null);
      setSubscribed(false);
      return;
    }
    const data = await apiClient.getProfile();
    applyAuthPayload(data);
  }, [applyAuthPayload]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      apiClient
        .getProfile()
        .then(applyAuthPayload)
        .catch(() => {
          localStorage.removeItem("token");
          setUser(null);
          setSubscribed(false);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [applyAuthPayload]);

  const login = async (email, password) => {
    const data = await apiClient.login(email, password);
    localStorage.setItem("token", data.access_token);
    applyAuthPayload(data);
    return data;
  };

  const register = async (email, password, name = "", phone = "") => {
    const data = await apiClient.register({ email, password, name, phone });
    localStorage.setItem("token", data.access_token);
    applyAuthPayload(data);
    return data;
  };

  const logout = () => {
    apiClient.logout();
    setUser(null);
    setSubscribed(false);
  };

  return (
    <AuthContext.Provider value={{ user, subscribed, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

export function formatErr(err) {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  return "An unexpected error occurred";
}
